/**
 * Action Panel Component
 * Displays services requiring provider assignment (assigned) - Full width layout
 *
 * Assign/Decline are REST endpoints (handled by parent callbacks).
 * Assign opens a provider picker filtered to possibleProviders ∩ organizationProviders.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import {
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Loader2,
  ExternalLink,
  Search,
  UserPlus,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Link } from 'react-router-dom'
import type { ProviderService } from '@/types/dashboard'
import { filterEligibleProvidersBySearch } from '@/utils/eligibleProviders'
import { decodeRelayId } from '@/utils/id'
import { useLocalization } from '@/contexts/LocalizationContext'

// ==================== Types ====================

interface ActionPanelProps {
  services?: ProviderService[]
  loading?: boolean
  onAssign?: (serviceId: string, providerProfileId: string) => Promise<void> | void
  onDecline?: (serviceId: string, reason?: string) => Promise<void> | void
  onAccept?: (serviceId: string) => Promise<void> | void
}

type CompletedAction = { type: 'assigned' | 'declined' | 'accepted' }

// Entity assignment queue statuses that surface as actionable cards.
const ASSIGNABLE_STATUSES = new Set(['PENDING', 'SEARCHING', 'ASSIGN_MANUALLY', 'ASSIGNED'])

// ==================== Component ====================

export function ActionPanel({
  services = [],
  loading = false,
  onAssign,
  onDecline,
  onAccept,
}: ActionPanelProps) {
  const { t } = useLocalization()
  const [processingServiceId, setProcessingServiceId] = useState<string | null>(null)
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [assignService, setAssignService] = useState<ProviderService | null>(null)
  const [selectedProviderId, setSelectedProviderId] = useState<string | null>(null)
  const [providerSearch, setProviderSearch] = useState('')
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [rejectServiceId, setRejectServiceId] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [actionType, setActionType] = useState<'assign' | 'reject' | 'accept' | null>(null)
  const [removedServiceIds, setRemovedServiceIds] = useState<Set<string>>(new Set())
  const [completedServices, setCompletedServices] = useState<Map<string, CompletedAction>>(new Map())

  const actionable = services.filter(
    (s) =>
      ASSIGNABLE_STATUSES.has((s.status || '').toUpperCase().trim()) &&
      s.canEntityAssign !== false &&
      !removedServiceIds.has(s.id),
  )
  const count = actionable.length

  const eligibleProviders = useMemo(() => {
    if (!assignService) return []
    // Entity assignment queue supplies a backend-filtered, entity-scoped provider list.
    return (assignService.assignableProviders ?? []).map((p) => ({
      // POST entity-assign-provider expects a raw provider_profile_id UUID.
      profileId: decodeRelayId(p.id) ?? p.id,
      fullName: p.fullName?.trim() || '—',
      phoneNumber: p.phoneNumber ?? undefined,
    }))
  }, [assignService])

  const filteredProviders = useMemo(
    () => filterEligibleProvidersBySearch(eligibleProviders, providerSearch),
    [eligibleProviders, providerSearch],
  )

  useEffect(() => {
    setRemovedServiceIds((prev) => {
      const next = new Set([...prev].filter((id) => services.some((s) => s.id === id)))
      return next.size === prev.size ? prev : next
    })
  }, [services])

  const completeAndRemove = useCallback((serviceId: string, type: CompletedAction['type']) => {
    setCompletedServices((prev) => new Map(prev).set(serviceId, { type }))
    setTimeout(() => {
      setRemovedServiceIds((prev) => new Set(prev).add(serviceId))
      setCompletedServices((prev) => {
        const next = new Map(prev)
        next.delete(serviceId)
        return next
      })
    }, 600)
  }, [])

  const rollbackRemoval = useCallback((serviceId: string) => {
    setRemovedServiceIds((prev) => {
      const next = new Set(prev)
      next.delete(serviceId)
      return next
    })
    setCompletedServices((prev) => {
      const next = new Map(prev)
      next.delete(serviceId)
      return next
    })
  }, [])

  const openAssignDialog = (service: ProviderService) => {
    setAssignService(service)
    setSelectedProviderId(null)
    setProviderSearch('')
    setAssignDialogOpen(true)
  }

  const handleAssignConfirm = async () => {
    if (!assignService || !selectedProviderId) return
    const serviceId = assignService.id
    const providerProfileId = selectedProviderId

    setProcessingServiceId(serviceId)
    setActionType('assign')
    setAssignDialogOpen(false)
    try {
      await onAssign?.(serviceId, providerProfileId)
      completeAndRemove(serviceId, 'assigned')
    } catch {
      rollbackRemoval(serviceId)
    } finally {
      setProcessingServiceId(null)
      setActionType(null)
      setAssignService(null)
      setSelectedProviderId(null)
      setProviderSearch('')
    }
  }

  const handleAccept = async (serviceId: string) => {
    setProcessingServiceId(serviceId)
    setActionType('accept')
    try {
      await onAccept?.(serviceId)
      completeAndRemove(serviceId, 'accepted')
    } catch {
      rollbackRemoval(serviceId)
    } finally {
      setProcessingServiceId(null)
      setActionType(null)
    }
  }

  const openRejectDialog = (serviceId: string) => {
    setRejectServiceId(serviceId)
    setRejectReason('')
    setRejectDialogOpen(true)
  }

  const handleRejectConfirm = async () => {
    if (!rejectServiceId) return
    const serviceId = rejectServiceId
    const reason = rejectReason || undefined

    setProcessingServiceId(serviceId)
    setActionType('reject')
    setRejectDialogOpen(false)
    try {
      await onDecline?.(serviceId, reason)
      completeAndRemove(serviceId, 'declined')
    } catch {
      rollbackRemoval(serviceId)
    } finally {
      setProcessingServiceId(null)
      setActionType(null)
      setRejectServiceId(null)
      setRejectReason('')
    }
  }

  if (loading && services.length === 0) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-5 w-20" />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (!loading && actionable.length === 0) return null

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-500" />
              <h2 className="text-lg font-semibold">{t('dashboard.actionRequired')}</h2>
              <Badge variant="destructive" className="text-xs animate-pulse">
                {t('dashboard.pendingCount', { count })}
              </Badge>
            </div>
            <span className="text-sm text-muted-foreground">
              {t('dashboard.pendingAcceptance')}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {actionable.map((service) => {
              const isProcessing = processingServiceId === service.id
              const completed = completedServices.get(service.id)
              const deadline = service.acceptanceDeadline
                ? new Date(service.acceptanceDeadline)
                : null
              const isExpired = deadline ? deadline < new Date() : false
              const isDisabled = isExpired || isProcessing || !!completed

              const orderId = service.order?.id
              const orderNumber = service.order?.orderNumber || '—'
              const serviceName =
                service.serviceTemplate?.nameEn || service.serviceTemplate?.nameAr || 'Service'
              const scheduledStart = service.appointment?.scheduledStart
              const scheduledTime = scheduledStart
                ? new Date(scheduledStart).toLocaleString()
                : '—'
              // Provider already assigned but not yet accepted → accept/reject on their behalf.
              const requiresAcceptance = service.requiresAcceptance === true

              if (completed) {
                const isSuccess = completed.type === 'assigned' || completed.type === 'accepted'
                const successLabel =
                  completed.type === 'accepted'
                    ? t('dashboard.serviceAccepted')
                    : t('dashboard.providerAssigned')
                return (
                  <div
                    key={service.id}
                    className={cn(
                      'border rounded-lg p-4 transition-all duration-300 flex flex-col items-center justify-center gap-2 min-h-[180px]',
                      isSuccess ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200',
                    )}
                  >
                    {isSuccess ? (
                      <CheckCircle className="h-8 w-8 text-green-500" />
                    ) : (
                      <XCircle className="h-8 w-8 text-red-500" />
                    )}
                    <p
                      className={cn(
                        'text-sm font-medium',
                        isSuccess ? 'text-green-700' : 'text-red-700',
                      )}
                    >
                      {isSuccess ? successLabel : t('dashboard.decline') + 'd'}
                    </p>
                    <p className="text-xs text-muted-foreground">{serviceName}</p>
                  </div>
                )
              }

              return (
                <div
                  key={service.id}
                  className={cn(
                    'group flex flex-col rounded-xl border p-4 transition-all duration-200',
                    isExpired
                      ? 'bg-muted/40 border-border opacity-70'
                      : 'bg-card border-border hover:border-primary/40 hover:shadow-md',
                    isProcessing && 'opacity-70 pointer-events-none',
                  )}
                >
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium whitespace-nowrap',
                        isExpired
                          ? 'bg-gray-100 text-gray-600'
                          : 'bg-amber-100 text-amber-800',
                      )}
                    >
                      <span
                        className={cn(
                          'h-1.5 w-1.5 rounded-full',
                          isExpired ? 'bg-gray-400' : 'bg-amber-500',
                        )}
                      />
                      {isExpired
                        ? t('dashboard.expired')
                        : t('dashboard.pendingAcceptanceStatus')}
                    </span>
                    {orderId ? (
                      <Link
                        to={`/orders/${orderId}`}
                        className="flex items-center gap-1 min-w-0 text-xs text-muted-foreground hover:text-primary hover:underline"
                      >
                        <span className="truncate">{orderNumber}</span>
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </Link>
                    ) : (
                      <span className="text-xs text-muted-foreground truncate">{orderNumber}</span>
                    )}
                  </div>

                  <h3 className="font-semibold text-sm text-foreground mb-3 line-clamp-2 min-h-[40px]">
                    {serviceName}
                  </h3>

                  <div className="space-y-1.5 mb-4">
                    <div className="flex items-center text-xs text-muted-foreground">
                      <Clock className="h-3.5 w-3.5 me-1.5 shrink-0" />
                      <span className="truncate">{scheduledTime}</span>
                    </div>
                    {requiresAcceptance && service.providerName && (
                      <div className="flex items-center text-xs text-muted-foreground">
                        <UserPlus className="h-3.5 w-3.5 me-1.5 shrink-0" />
                        <span className="truncate">
                          {t('dashboard.assignedProvider')}: {service.providerName}
                        </span>
                      </div>
                    )}
                    {service.acceptanceDeadline && (
                      <div
                        className={cn(
                          'flex items-center text-xs',
                          isExpired ? 'text-gray-500' : 'text-orange-600',
                        )}
                      >
                        <AlertCircle className="h-3.5 w-3.5 me-1.5 shrink-0" />
                        <span className="truncate">
                          {isExpired ? t('dashboard.expired') : t('dashboard.deadline')}:{' '}
                          {new Date(service.acceptanceDeadline).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="mt-auto flex flex-col gap-2">
                    {requiresAcceptance ? (
                      <Button
                        size="sm"
                        className="w-full h-9"
                        onClick={() => handleAccept(service.id)}
                        disabled={isDisabled}
                      >
                        {isProcessing && actionType === 'accept' ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <CheckCircle className="h-4 w-4" />
                            <span className="truncate">{t('dashboard.accept')}</span>
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        className="w-full h-9"
                        onClick={() => openAssignDialog(service)}
                        disabled={isDisabled}
                      >
                        {isProcessing && actionType === 'assign' ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <UserPlus className="h-4 w-4" />
                            <span className="truncate">{t('dashboard.assignProvider')}</span>
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      className={cn(
                        'w-full h-9 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700',
                        isDisabled && 'opacity-50 cursor-not-allowed hover:bg-transparent',
                      )}
                      onClick={() => openRejectDialog(service.id)}
                      disabled={isDisabled}
                    >
                      {isProcessing && actionType === 'reject' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <XCircle className="h-4 w-4" />
                          <span className="truncate">
                            {requiresAcceptance ? t('dashboard.reject') : t('dashboard.decline')}
                          </span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Assign Provider Dialog */}
      <Dialog
        open={assignDialogOpen}
        onOpenChange={(open) => {
          setAssignDialogOpen(open)
          if (!open) {
            setAssignService(null)
            setSelectedProviderId(null)
            setProviderSearch('')
          }
        }}
      >
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>{t('dashboard.assignProviderTitle')}</DialogTitle>
            <DialogDescription>{t('dashboard.assignProviderDescription')}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {eligibleProviders.length > 0 && (
              <div className="relative">
                <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('dashboard.searchProviders')}
                  value={providerSearch}
                  onChange={(e) => setProviderSearch(e.target.value)}
                  className="ps-9"
                />
              </div>
            )}

            {eligibleProviders.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {t('dashboard.noEligibleProviders')}
              </p>
            ) : filteredProviders.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                {t('dashboard.noProvidersMatchSearch')}
              </p>
            ) : (
              <RadioGroup
                value={selectedProviderId ?? ''}
                onValueChange={setSelectedProviderId}
                className="max-h-[240px] overflow-y-auto gap-2 pe-1"
              >
                {filteredProviders.map((provider) => (
                  <label
                    key={provider.profileId}
                    htmlFor={`provider-${provider.profileId}`}
                    className={cn(
                      'flex items-center gap-3 rounded-lg border p-3 cursor-pointer transition-colors',
                      selectedProviderId === provider.profileId
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50',
                    )}
                  >
                    <RadioGroupItem
                      value={provider.profileId}
                      id={`provider-${provider.profileId}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{provider.fullName}</p>
                      {provider.phoneNumber && (
                        <p className="text-xs text-muted-foreground">{provider.phoneNumber}</p>
                      )}
                    </div>
                  </label>
                ))}
              </RadioGroup>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleAssignConfirm}
              disabled={!selectedProviderId || eligibleProviders.length === 0}
            >
              {t('dashboard.confirmAssignProvider')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Confirmation Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('dashboard.declineService')}</DialogTitle>
            <DialogDescription>{t('dashboard.declineServiceConfirm')}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">
              {t('dashboard.reasonOptional')}
            </label>
            <Textarea
              placeholder={t('dashboard.enterReasonForDeclining')}
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="resize-none"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleRejectConfirm}>
              {t('dashboard.declineService')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
